import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, ShieldCheck, FileCheck2, Award, HeartHandshake } from "lucide-react";

const STATUS_COPY = {
  pending: { label: "Not submitted", desc: "Upload your documents below to submit.", color: "bg-muted" },
  submitted: { label: "Under review", desc: "We'll notify you once admin approves.", color: "bg-brand-yellow/20" },
  approved: { label: "Approved ✓", desc: "Your listings are live for customers.", color: "bg-green-100 text-green-800" },
  rejected: { label: "Rejected", desc: "Please re-submit with correct documents.", color: "bg-brand-orange/20" },
  expired: { label: "Certification expired", desc: "Your certificate has expired — please renew and re-submit.", color: "bg-brand-orange/20" },
};

const CERT_TYPES = [
  { value: "nursing", label: "Nursing / GNM / ANM (Caregiver)" },
  { value: "cpr_first_aid", label: "CPR / First-Aid (Caregiver)" },
  { value: "food_safety", label: "Food Safety / FSSAI (Cook)" },
  { value: "commercial_dl", label: "Commercial Driving Licence (Driver)" },
  { value: "iti", label: "ITI Certification (Electrician, Plumber, Mason, Carpenter)" },
  { value: "teaching", label: "Teaching / B.Ed (Home Tutor)" },
  { value: "other", label: "Other" },
];

export default function ProviderKyc() {
  const { provider, refresh } = useAuth();
  const kyc = provider?.kyc || {};
  const status = kyc.status || "pending";
  const cert = kyc.certification || {};

  const [form, setForm] = useState({
    aadhaar_number: kyc.aadhaar_number || "",
    driving_license: kyc.driving_license || "",
    voter_id: kyc.voter_id || "",
    bio: provider?.bio || "",
    service_area: provider?.service_area || "",
    skills: (provider?.skills || []).join(", "),
    cert_type: cert.type || "",
    cert_number: cert.number || "",
    cert_authority: cert.authority || "",
    experience_years: cert.experience_years != null ? String(cert.experience_years) : "",
    cert_expires_at: cert.expires_at || "",
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => v != null && data.append(k, v));
      files.forEach((f) => data.append("documents", f));
      await api.post("/provider/kyc", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("KYC submitted for review");
      await refresh();
      setFiles([]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const copy = STATUS_COPY[status];

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="provider-kyc-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">KYC verification</h1>
        <p className="mt-1 text-sm text-muted-foreground">Upload authentic docs to activate listings.</p>
      </div>

      <div className={`rounded-2xl border p-4 ${copy.color}`} data-testid="kyc-status">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} />
          <div className="font-heading text-sm font-bold uppercase tracking-widest">Status: {copy.label}</div>
        </div>
        <div className="mt-1 text-sm text-foreground/80">{copy.desc}</div>
        {kyc.rejection_reason && status === "rejected" && (
          <div className="mt-2 text-xs">Reason: <b>{kyc.rejection_reason}</b></div>
        )}
      </div>

      <form onSubmit={submit} className="space-y-5 rounded-3xl border border-foreground/10 bg-white p-6">
        <div>
          <div className="font-heading text-lg font-bold">Profile</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label>Service area</Label>
              <Input value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} placeholder="e.g. Bengaluru, HSR Layout" data-testid="kyc-area" />
            </div>
            <div className="space-y-1"><Label>Skills (comma-separated)</Label>
              <Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="Cleaning, Plumbing" data-testid="kyc-skills" />
            </div>
          </div>
          <div className="mt-3 space-y-1"><Label>Short bio</Label>
            <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Tell customers about your experience" data-testid="kyc-bio" />
          </div>
        </div>

        <hr />

        <div>
          <div className="font-heading text-lg font-bold">Identity numbers</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div className="space-y-1"><Label>Aadhaar</Label>
              <Input value={form.aadhaar_number} onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value })} placeholder="XXXX-XXXX-XXXX" data-testid="kyc-aadhaar" />
            </div>
            <div className="space-y-1"><Label>Driving Licence</Label>
              <Input value={form.driving_license} onChange={(e) => setForm({ ...form, driving_license: e.target.value })} data-testid="kyc-dl" />
            </div>
            <div className="space-y-1"><Label>Voter ID</Label>
              <Input value={form.voter_id} onChange={(e) => setForm({ ...form, voter_id: e.target.value })} data-testid="kyc-voter" />
            </div>
          </div>
        </div>

        <hr />

        {/* Professional certification — required for caregivers, cooks, drivers etc. */}
        <div>
          <div className="flex items-center gap-2">
            <Award size={16} className="text-brand-orange" />
            <div className="font-heading text-lg font-bold">Professional certification</div>
            <span className="rounded-full bg-brand-orange px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">Required</span>
          </div>
          <div className="mt-2 rounded-xl border border-brand-orange/25 bg-brand-orange/5 p-3 text-xs text-foreground/80">
            <div className="flex items-start gap-2">
              <HeartHandshake size={14} className="mt-0.5 shrink-0 text-brand-orange" />
              <span>
                Every provider must declare a professional credential relevant to the services they offer:
                Caregivers → <b>Nursing / CPR / First-Aid</b>. Cooks → <b>FSSAI / Food Safety</b>.
                Commercial drivers → <b>Commercial DL</b>. Skilled workers → <b>ITI certification</b>.
                Tutors → <b>Teaching / B.Ed</b>. Attach the certificate scan under "Upload documents" below.
              </span>
            </div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Certification type</Label>
              <Select value={form.cert_type} onValueChange={(v) => setForm({ ...form, cert_type: v })}>
                <SelectTrigger data-testid="kyc-cert-type"><SelectValue placeholder="Select certification" /></SelectTrigger>
                <SelectContent>
                  {CERT_TYPES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Certificate / registration number</Label>
              <Input value={form.cert_number} onChange={(e) => setForm({ ...form, cert_number: e.target.value })} placeholder="e.g. KA-NRC-2023-000123" data-testid="kyc-cert-number" />
            </div>
            <div className="space-y-1">
              <Label>Issuing authority</Label>
              <Input value={form.cert_authority} onChange={(e) => setForm({ ...form, cert_authority: e.target.value })} placeholder="Karnataka Nursing Council" data-testid="kyc-cert-authority" />
            </div>
            <div className="space-y-1">
              <Label>Years of experience</Label>
              <Input type="number" min={0} value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} data-testid="kyc-experience" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label>Certificate expiry date</Label>
              <Input type="date" value={form.cert_expires_at} onChange={(e) => setForm({ ...form, cert_expires_at: e.target.value })} data-testid="kyc-cert-expiry" />
              <p className="text-[11px] text-muted-foreground">You'll be prompted to renew a month before this date. Listings are paused if the certificate expires.</p>
            </div>
          </div>
        </div>

        <div>
          <div className="font-heading text-lg font-bold">Upload documents</div>
          <label className="mt-3 flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-foreground/15 p-8 text-center transition-colors hover:bg-muted" data-testid="kyc-upload-zone">
            <Upload size={20} className="text-muted-foreground" />
            <div className="text-sm font-semibold">Click to upload images / PDFs</div>
            <div className="text-xs text-muted-foreground">Aadhaar, DL, Voter ID and certification scans — max 5 files</div>
            <input
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files).slice(0, 5))}
              data-testid="kyc-file-input"
            />
          </label>
          {files.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-muted-foreground"><FileCheck2 size={14} className="text-brand-orange" /> {f.name}</li>
              ))}
            </ul>
          )}
          {kyc.documents?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Already uploaded</div>
              <ul className="mt-1 text-xs">
                {kyc.documents.map((d, i) => (
                  <li key={i}><a href={`${process.env.REACT_APP_BACKEND_URL}${d}`} target="_blank" rel="noreferrer" className="text-brand-orange underline">Document {i + 1}</a></li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <Button type="submit" disabled={loading} className="h-11 w-full rounded-full bg-brand-orange text-white hover:bg-brand-orange/90" data-testid="submit-kyc">
          {loading ? "Submitting..." : "Submit for verification"}
        </Button>
      </form>
    </div>
  );
}
